import { ReactNode, useCallback, useEffect, useState } from 'react'
import { Input, Modal, Radio, Typography } from '@musinsa/pbo-core'
import { DOWNLOAD_REASON_EMPTY_MESSAGE } from '@/utils/consts'
import { REQUIRED_MARK_COLOR } from '@/pages/inventory/inventoryHistory/consts'

/** 상세사유 최소 길이 — 1.0 RemarkPop 검증과 동일. */
const REASON_DETAIL_MIN_LENGTH = 5

/** 상세사유 최대 길이 — ST02 모달과 동일. `{사유코드}_{상세사유}` 가 감사로그 REMARK 로 들어간다. */
const REASON_DETAIL_MAX_LENGTH = 200

interface Props {
  open: boolean
  /** 사유코드 옵션 — GET /api/v1/combos(DOWNLOAD_CD) 응답(호출부에서 주입). */
  reasonOptions: { value: string; label: string }[]
  onConfirm: (params: { reasonCode: string; reasonDetail: string }) => void
  onCancel: () => void
}

/**
 * IV02 엑셀 다운로드 사유 모달.
 *
 * ST02 `ExcelDownloadReasonModal` 과 같은 구성이지만 사유코드를 1.0 `/api/common/combo` 대신
 * 2.0 콤보에서 받는다(LOGISPRD-13947). 공용 `ExcelDownloadModal` 의 파일 비밀번호는 받지 않는다 —
 * IV02 엑셀은 `Excel.download` 로 만들고 비밀번호를 쓰지 않아 원래 평문이었다.
 */
export function ExcelDownloadReasonModal({ open, reasonOptions, onConfirm, onCancel }: Props) {
  const [reasonCode, setReasonCode] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')

  // 열 때마다 초기화 — 모달이 계속 마운트된 채 `open` 만 바뀌므로 비우지 않으면 이전 사유가 남는다.
  useEffect(() => {
    if (!open) return
    setReasonCode('')
    setReasonDetail('')
  }, [open])

  const isValid = Boolean(reasonCode) && reasonDetail.trim().length >= REASON_DETAIL_MIN_LENGTH

  const handleOk = useCallback(() => {
    if (!isValid) return
    onConfirm({ reasonCode, reasonDetail: reasonDetail.trim() })
  }, [isValid, reasonCode, reasonDetail, onConfirm])

  return (
    <Modal open={open} title="다운로드 사유" width={560} okText="확인" cancelText="취소" okDisabled={!isValid} onOk={handleOk} onCancel={onCancel}>
      <div>
        <FieldLabel>사유코드</FieldLabel>
        {reasonOptions.length === 0 ? (
          <Typography.Text type="danger">{DOWNLOAD_REASON_EMPTY_MESSAGE}</Typography.Text>
        ) : (
          <Radio.Group
            value={reasonCode}
            onChange={(e) => setReasonCode(String(e.target.value))}
            style={{ marginTop: 8, display: 'flex', flexWrap: 'nowrap', gap: 16, whiteSpace: 'nowrap' }}
          >
            {reasonOptions.map((o) => (
              <Radio key={o.value} value={o.value}>
                {o.label}
              </Radio>
            ))}
          </Radio.Group>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <FieldLabel>상세사유</FieldLabel>
        <Input.TextArea
          rows={3}
          placeholder="사유를 입력하세요"
          value={reasonDetail}
          onChange={(e) => setReasonDetail(e.target.value)}
          maxLength={REASON_DETAIL_MAX_LENGTH}
          showCount
          style={{ marginTop: 8 }}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {`${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해주세요.`}
        </Typography.Text>
      </div>
    </Modal>
  )
}

/** 필수 표시(*)가 붙은 필드 라벨 — 사유코드·상세사유 둘 다 필수다. */
function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <Typography.Text>
      {children} <span style={{ color: REQUIRED_MARK_COLOR }}>*</span>
    </Typography.Text>
  )
}
